// BUILD-046 J1a: the small drawings of a task's Customer. The rules (what shows, which words) are in customerDisplayModel.ts.
//   CustomerText  : the customer name, with a quiet note after it when the name is the last known one, the customer was not found, or it is inactive.
//   customerPart  : the customer as the FIRST part of the Comfortable quiet second line (null when the row has no customer or the density is Compact: Compact has the column).
//   associationParts : the customer, then the row's job, as the first parts of that line (the rows use this one; the job is written "Job: <name>" when the row has a customer too, and, while
//                      customers are in play, when it has no customer either).
import type { ReactNode } from 'react';
import type { Density } from './densityStore';
import { CUSTOMER_LABEL, JOB_LABEL, JOB_LINE_PREFIX, customerInSecondLine, customerLineOf, customerOf } from './customerDisplayModel';
import type { CustomerShown } from './customerDisplayModel';
import { jobOf } from './jobDisplayModel';
import { JobText } from './JobParts';
import { TruncatedText } from './TruncatedText';

export function CustomerText({ customer, truncate = false }: { customer: CustomerShown; truncate?: boolean }) {
  const line = customerLineOf(customer);
  const body = (
    <>
      <span className="font-medium text-gray-800">{line.label}</span>
      {line.note !== null && <span className="text-gray-500"> ({line.note})</span>}
    </>
  );
  // Where the column cuts the name short (Compact), the full name is on hover AND on keyboard focus (TruncatedText); elsewhere it is plain text with the same hover text as before.
  if (truncate) return <TruncatedText text={line.title} block testId="customer-text">{body}</TruncatedText>;
  return (
    <span title={line.title} data-testid="customer-text">
      {body}
    </span>
  );
}

/** The customer for the quiet second line of a Comfortable row; null when there is none or in Compact. */
export function customerPart(task: unknown, density: Density): ReactNode {
  if (!customerInSecondLine(density)) return null;
  const customer = customerOf(task);
  return customer === null ? null : <CustomerText key="customer" customer={customer} />;
}

/**
 * The association parts of the Comfortable quiet second line, in the order they are read: the customer name first, then the job name when the row has one (BUILD-046 Customer
 * association). A job that sits beside a customer is written "Job: <name>" so the two never read as two customers; a job on a row with no customer is exactly what it was (the
 * plain job name). Compact has the column instead, so it returns nothing. A row shown in its minimal form has neither (customerOf / jobOf return null for it).
 *
 * CHANGED 2026-10-25 (BUILD-046 customer association, Owner/Builder direction: a job must never read as a customer): `customersInPlay` says the page is showing customers. While it is true, a row
 * that has a JOB and NO customer writes the job as "Job: <name>" too, so the bare name in the slot where the other rows show a customer name is always a customer. While it is false (or absent) a
 * job-only row is exactly what it was: the plain job name.
 */
export function associationParts(task: unknown, density: Density, options: { jobFirst?: boolean; customersInPlay?: boolean } = {}): ReactNode[] {
  if (!customerInSecondLine(density)) return [];
  const customer = customerOf(task);
  const job = jobOf(task);
  // Team and Company (BUILD-046 Team table cleanup): the JOB first, then the customer, and when the row has BOTH each says which it is ("Job: ..." then "Customer: ...") so two names side by side never
  // read as one. A row with only one of them is exactly what it was (the plain name). No other table asks for this order.
  if (options.jobFirst === true) {
    if (job !== null && customer !== null) {
      return [
        <span key="job">
          <span className="text-gray-600">{JOB_LABEL} </span>
          <JobText job={job} />
        </span>,
        <span key="customer">
          <span className="text-gray-600">{CUSTOMER_LABEL} </span>
          <CustomerText customer={customer} />
        </span>,
      ];
    }
    if (job !== null && options.customersInPlay === true) {
      // CHANGED 2026-10-25: customers are in play and this row has a job but no customer: the job says it is a job.
      return [
        <span key="job">
          <span className="text-gray-600">{JOB_LABEL} </span>
          <JobText job={job} />
        </span>,
      ];
    }
    return [job === null ? null : <JobText key="job" job={job} />, customer === null ? null : <CustomerText key="customer" customer={customer} />];
  }
  const jobNode =
    job === null ? null : customer === null && options.customersInPlay !== true ? (
      <JobText key="job" job={job} />
    ) : (
      <span key="job">
        <span className="text-gray-600">{JOB_LINE_PREFIX}</span>
        <JobText job={job} />
      </span>
    );
  return [customer === null ? null : <CustomerText key="customer" customer={customer} />, jobNode];
}
